import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { MapPin, Bed, Home } from 'lucide-react';
import Navbar from './components/Navbar';
import PropertySidebar from './components/PropertySidebar';
import AccommodationCard from './components/AccommodationCard';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import SignUpModal from './components/SignUpModal';
import ProfileModal from './components/ProfileModal';
import ChatsModal from './components/ChatsModal';
import SettingsModal from './components/SettingsModal';
import ListingsModal from './components/ListingsModal';
import PropertyDetailModal from './components/PropertyDetailModal';
import SkeletonCard from './components/SkeletonCard';
import api from './services/api';
import { createPropertySlug } from './utils/slugify';
import { seoConfig, generateSEOMetadata } from './config/seoData';

function SEOLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [user, setUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatPropertyId, setChatPropertyId] = useState(null);
  const [chatReceiverId, setChatReceiverId] = useState(null);
  const [displayCount, setDisplayCount] = useState(20);
  const [searching, setSearching] = useState(false);
  const [relatedSearches, setRelatedSearches] = useState([]);
  const [propertyModalLoading, setPropertyModalLoading] = useState(false);

  const showAuthModal = location.pathname.endsWith('/signin');
  const showSignUpModal = location.pathname.endsWith('/signup');
  const showProfileModal = location.pathname.endsWith('/profile');
  const showListingsModal = location.pathname.endsWith('/listings');
  const showChatsModal = location.pathname.endsWith('/chats');
  const showSettingsModal = location.pathname.endsWith('/settings');
  const showPropertyDetail = location.pathname.includes('/property/');

  const normalizedCitySlug = params.city?.toLowerCase();
  const normalizedPropertyTypeSlug = params.propertyType?.toLowerCase();
  const normalizedAreaSlug = params.area?.toLowerCase();
  const normalizedBedroomSlug = params.bedroomSlug?.toLowerCase();

  const cityObj = seoConfig.cities.find((item) => item.slug === normalizedCitySlug);
  const propertyTypeObj = seoConfig.propertyTypes.find((item) => item.slug === normalizedPropertyTypeSlug);
  const possibleArea = cityObj && seoConfig.areas[cityObj.slug]
    ? seoConfig.areas[cityObj.slug].find((item) => item.slug === normalizedAreaSlug)
    : null;
  const directBedroom = seoConfig.bedrooms.find((item) => item.slug === normalizedAreaSlug);
  const bedroomObj = seoConfig.bedrooms.find((item) => item.slug === normalizedBedroomSlug) || directBedroom || null;
  const areaObj = params.bedroomSlug ? possibleArea : null;
  const baseSeoPath = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const stopSegments = new Set(['property', 'signin', 'signup', 'profile', 'listings', 'chats', 'settings']);

    if (pathSegments[0] === 'rent') {
      const seoSegments = [];
      for (const segment of pathSegments) {
        if (stopSegments.has(segment)) {
          break;
        }
        seoSegments.push(segment);
      }

      if (seoSegments.length >= 3) {
        return `/${seoSegments.join('/')}`;
      }
    }

    return `/rent/${params.propertyType}/${params.city}`;
  }, [location.pathname, params.propertyType, params.city]);

  const metadata = useMemo(() => generateSEOMetadata({
    city: cityObj,
    area: areaObj,
    propertyType: propertyTypeObj,
    bedrooms: bedroomObj
  }), [cityObj, areaObj, propertyTypeObj, bedroomObj]);

  useEffect(() => {
    document.title = metadata.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', metadata.description);
    } else {
      const tag = document.createElement('meta');
      tag.name = 'description';
      tag.content = metadata.description;
      document.head.appendChild(tag);
    }

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', `${window.location.origin}${location.pathname}`);
  }, [metadata, location.pathname]);

  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${window.location.origin}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Rent',
        item: `${window.location.origin}/rent`
      },
      propertyTypeObj ? {
        '@type': 'ListItem',
        position: 3,
        name: propertyTypeObj.plural,
        item: `${window.location.origin}/rent/${propertyTypeObj.slug}/${cityObj?.slug || ''}`
      } : null,
      cityObj ? {
        '@type': 'ListItem',
        position: propertyTypeObj ? 4 : 3,
        name: cityObj.name,
        item: canonicalUrl
      } : null
    ].filter(Boolean)
  }), [propertyTypeObj, cityObj, canonicalUrl]);

  const collectionSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: metadata.h1,
    description: metadata.description,
    url: canonicalUrl,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: properties.length,
      itemListElement: properties.slice(0, 10).map((property, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${window.location.origin}/property/${createPropertySlug(property.title || property.name, property.id)}`,
        name: property.title || property.name
      }))
    }
  }), [metadata, canonicalUrl, properties]);

  useEffect(() => {
    let breadcrumbScript = document.getElementById('seo-breadcrumb-schema');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.id = 'seo-breadcrumb-schema';
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    let collectionScript = document.getElementById('seo-collection-schema');
    if (!collectionScript) {
      collectionScript = document.createElement('script');
      collectionScript.type = 'application/ld+json';
      collectionScript.id = 'seo-collection-schema';
      document.head.appendChild(collectionScript);
    }
    collectionScript.textContent = JSON.stringify(collectionSchema);

    return () => {
      const staleBreadcrumb = document.getElementById('seo-breadcrumb-schema');
      const staleCollection = document.getElementById('seo-collection-schema');
      if (staleBreadcrumb) staleBreadcrumb.remove();
      if (staleCollection) staleCollection.remove();
    };
  }, [breadcrumbSchema, collectionSchema]);

  const checkAuth = async () => {
    try {
      const userData = await api.getUser();
      if (userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  const fetchAndShowProperty = async (propertyId) => {
    try {
      setPropertyModalLoading(true);
      const property = await api.getPropertyById(propertyId);
      setSelectedProperty(property);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      navigate(baseSeoPath, { replace: true });
    } finally {
      setPropertyModalLoading(false);
    }
  };

  const fetchSEOProperties = async () => {
    try {
      setLoading(true);
      const response = await api.getSEOProperties({
        city: cityObj?.name,
        area: areaObj?.name,
        propertyType: propertyTypeObj?.name,
        bedrooms: bedroomObj?.count
      });
      setProperties(response.properties || []);
      setDisplayCount(20);
    } catch (error) {
      console.error('Failed to fetch SEO properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedSearches = async () => {
    try {
      const response = await api.getSEORelatedSearches({
        city: cityObj?.slug,
        propertyType: propertyTypeObj?.slug,
        bedrooms: bedroomObj?.count
      });
      setRelatedSearches(response.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch related searches:', error);
      setRelatedSearches([]);
    }
  };

  useEffect(() => {
    if (params.id && showPropertyDetail) {
      fetchAndShowProperty(params.id);
    } else {
      setSelectedProperty(null);
    }
  }, [params.id, showPropertyDetail, baseSeoPath]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      localStorage.setItem('accessToken', token);
      window.history.replaceState({}, document.title, window.location.pathname);
      checkAuth();
    } else {
      checkAuth();
    }

    fetchSEOProperties();
    fetchRelatedSearches();
  }, [params.city, params.area, params.propertyType, params.bedroomSlug]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAuth();
    }, 500);
    return () => clearTimeout(timer);
  }, [location]);

  const handleSignOut = async () => {
    try {
      await api.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSaveProfile = async (profileData) => {
    const updatedUser = await api.updateProfile(profileData);
    setUser(updatedUser);
    navigate(baseSeoPath);
    return updatedUser;
  };

  const handlePropertyClick = (property) => {
    setPropertyModalLoading(true);
    const slug = createPropertySlug(property.title || property.name, property.id);
    navigate(`${baseSeoPath}/property/${slug}`);
  };

  const handleContactOwner = (property, owner) => {
    if (!user) {
      navigate(`${baseSeoPath}/signin`);
      return;
    }
    setChatPropertyId(property.id);
    setChatReceiverId(owner?.id || property.user_id);
    navigate(`${baseSeoPath}/chats`);
  };

  const handleListProperty = () => {
    if (user) {
      navigate(`${baseSeoPath}/listings`);
    } else {
      navigate(`${baseSeoPath}/signin`);
    }
  };

  const handleSearch = async (query) => {
    setSearching(true);
    await new Promise((resolve) => setTimeout(resolve, 400));
    navigate('/');
    setSearching(false);
  };

  const handlePropertyTypeSelect = (type) => {
    if (!cityObj) return;
    const selectedType = seoConfig.propertyTypes.find((item) => item.name === type) || propertyTypeObj;
    if (!selectedType) return;
    navigate(`/rent/${selectedType.slug}/${cityObj.slug}`);
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Rent', href: '/' },
    propertyTypeObj && cityObj ? { label: propertyTypeObj.plural, href: `/rent/${propertyTypeObj.slug}/${cityObj.slug}` } : null,
    cityObj ? { label: cityObj.name, href: location.pathname } : null
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Toaster position="top-right" />
      <div className="fixed top-0 left-0 right-0 h-32 sm:h-24 bg-gradient-to-br from-blue-50 via-white to-blue-50 z-40"></div>
      <Navbar
        user={user}
        onSignIn={() => navigate(`${baseSeoPath}/signin`)}
        onRegister={() => navigate(`${baseSeoPath}/signup`)}
        onSignOut={handleSignOut}
        onProfileClick={() => navigate(`${baseSeoPath}/profile`)}
        onListingsClick={() => navigate(`${baseSeoPath}/listings`)}
        onChatsClick={() => navigate(`${baseSeoPath}/chats`)}
        onSettingsClick={() => navigate(`${baseSeoPath}/settings`)}
        onSearch={handleSearch}
        onListProperty={handleListProperty}
        searching={searching}
      />
      <AuthModal isOpen={showAuthModal} onClose={() => navigate(baseSeoPath)} onSwitchToSignUp={() => navigate(`${baseSeoPath}/signup`)} />
      <SignUpModal isOpen={showSignUpModal} onClose={() => navigate(baseSeoPath)} onSwitchToSignIn={() => navigate(`${baseSeoPath}/signin`)} />
      <ProfileModal isOpen={showProfileModal} onClose={() => navigate(baseSeoPath)} user={user} onSave={handleSaveProfile} />
      <ListingsModal isOpen={showListingsModal} onClose={() => navigate(baseSeoPath)} user={user} onListingChange={fetchSEOProperties} />
      <ChatsModal isOpen={showChatsModal} onClose={() => { navigate(baseSeoPath); setChatPropertyId(null); setChatReceiverId(null); }} user={user} initialPropertyId={chatPropertyId} initialReceiverId={chatReceiverId} />
      <SettingsModal isOpen={showSettingsModal} onClose={() => navigate(baseSeoPath)} user={user} />
      <PropertyDetailModal isOpen={showPropertyDetail} onClose={() => navigate(baseSeoPath)} property={selectedProperty} onContactOwner={handleContactOwner} />
      {propertyModalLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <aside className="w-full sm:w-16 flex-shrink-0">
            <div className="relative sm:fixed sm:top-28 sm:left-6 z-40">
              <PropertySidebar selectedType={propertyTypeObj?.name || null} onTypeSelect={handlePropertyTypeSelect} />
            </div>
          </aside>

          <main className="flex-1">
            <div className="pb-4 sm:pb-6 mb-4 sm:mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-32 flex items-center justify-center hover:border-gray-300 transition-colors duration-200">
                  <p className="text-gray-400 text-sm font-medium">Advertise Here</p>
                </div>
                <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-32 flex items-center justify-center hover:border-gray-300 transition-colors duration-200">
                  <p className="text-gray-400 text-sm font-medium">Advertise Here</p>
                </div>
              </div>
            </div>

            <div className="mb-5 sm:mb-6">
              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500 mb-3">
                {breadcrumbItems.map((item, index) => (
                  <React.Fragment key={item.href}>
                    {index > 0 && <span>/</span>}
                    <button onClick={() => navigate(item.href)} className="hover:text-blue-600 transition-colors">{item.label}</button>
                  </React.Fragment>
                ))}
              </div>
              <h1 className="text-xl sm:text-2xl font-light text-gray-700 mb-2 leading-snug">{metadata.h1}</h1>
              <p className="text-sm text-gray-500 leading-6">{metadata.description}</p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 text-xs sm:text-sm">
                {cityObj && <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full"><MapPin className="w-4 h-4" />{cityObj.name}</span>}
                {bedroomObj && <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full"><Bed className="w-4 h-4" />{bedroomObj.name}</span>}
                {propertyTypeObj && <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full"><Home className="w-4 h-4" />{propertyTypeObj.plural}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative z-30">
              {loading || searching ? (
                [...Array(8)].map((_, index) => <SkeletonCard key={index} />)
              ) : properties.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500">No properties Found.</p>
                </div>
              ) : (
                properties.slice(0, displayCount).map((property) => (
                  <AccommodationCard
                    key={property.id}
                    accommodation={{ ...property, name: property.title || property.name, type: property.property_type || property.type }}
                    onClick={() => handlePropertyClick(property)}
                  />
                ))
              )}
            </div>

            {!loading && properties.length > displayCount && (
              <div className="flex justify-center mt-8">
                <button onClick={() => setDisplayCount((prev) => prev + 20)} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl">
                  Load More Properties ({properties.length - displayCount} remaining)
                </button>
              </div>
            )}

            {relatedSearches.length > 0 && (
              <div className="mt-8 sm:mt-10 bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Related searches</h2>
                <div className="flex flex-wrap gap-3">
                  {relatedSearches.map((item) => (
                    <button key={item.path} onClick={() => navigate(item.path)} className="px-4 py-2 rounded-full bg-gray-100 hover:bg-blue-50 hover:text-blue-700 text-sm text-gray-700 transition-colors">
                      {item.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 pr-0 sm:pr-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <aside className="hidden sm:block w-16 flex-shrink-0"></aside>
          <div className="flex-1">
            <Footer onListYourProperty={handleListProperty} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SEOLandingPage;
